import { useQuery } from 'react-query';

import type { OrganizationJob } from '../types';

type UseOrganizationJobsHandler = {
  data: Array<OrganizationJob>;
  error: string | null;
  isLoading: boolean;
};

type UseOrganizationJobsInput = {
  fromDate: string;
  toDate: string;
  assignedStatus?: 'open' | 'assigned';
  jobType?: 'interest' | 'urgent' | 'personal' | undefined;
  selectedFreelancers?: Array<number>;
  selectedLocations?: Array<number>;
  selectedOrganizations?: Array<number>;
};

const useOrganizationJobs = (
  params: UseOrganizationJobsInput,
): UseOrganizationJobsHandler => {
  const { isLoading, error, data } = useQuery<Array<OrganizationJob>, string>(
    [
      'organization-jobs',
      params.fromDate,
      params.toDate,
      params.assignedStatus,
      params.jobType,
      params.selectedFreelancers,
      params.selectedLocations,
      params.selectedOrganizations,
    ],
    async () => {
      const query = new URLSearchParams();
      query.append('from_date', params.fromDate);
      query.append('to_date', params.toDate);

      if (params.assignedStatus) {
        query.append('assigned_status', params.assignedStatus);
      }

      if (params.jobType) {
        query.append('job_type', params.jobType);
      }

      if (
        typeof params?.selectedFreelancers === 'object' &&
        params?.selectedFreelancers?.length > 0
      ) {
        params?.selectedFreelancers.forEach((item) =>
          query.append('assigned_to', item.toString()),
        );
      }

      if (
        typeof params?.selectedOrganizations === 'object' &&
        params?.selectedOrganizations?.length > 0
      ) {
        params?.selectedOrganizations.forEach((item) =>
          query.append('organisation', item.toString()),
        );
      }

      if (
        typeof params?.selectedLocations === 'object' &&
        params?.selectedLocations?.length > 0
      ) {
        params?.selectedLocations.forEach((item) =>
          query.append('location', item.toString()),
        );
      }

      const response = await fetch(
        `/api/organisation/jobs?${new URLSearchParams(query)}`,
      );
      if (response.ok) return response.json();
      throw new Error('Something went wrong');
    },
    {
      retry: false,
    },
  );
  return { data: data || [], error, isLoading };
};

export default useOrganizationJobs;
